import { isAxiosError } from "axios";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslations } from "next-intl";
import { Button, Field, Heading, Input, PageShell } from "@/components/ui";
import { TenantScopeShell } from "@/components/tenant-scope-shell";
import { parseSchoolAddress } from "@/lib/school-address";
import { fetchTenantBootstrap } from "@/services/api/tenants";

/**
 * MT-1 Plan 6 S16 (FR-20): the neutral page a signed-out visitor sees at `/`: type the address
 * your school gave you. No school's name, logo or colour, and no platform name. The address is
 * checked against the school's public bootstrap before navigating.
 */
export function FindYourSchool() {
  const t = useTranslations("home.findSchool");
  const navigate = useNavigate();
  const [value, setValue] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function onSubmit(event: React.FormEvent) {
    event.preventDefault();
    const slug = parseSchoolAddress(value);
    if (!slug) {
      setError(t("invalid"));
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await fetchTenantBootstrap(slug);
      navigate(`/t/${slug}/login`);
    } catch (err) {
      setError(isAxiosError(err) && err.response?.status === 404 ? t("notFound") : t("failed"));
    } finally {
      setBusy(false);
    }
  }

  return (
    <TenantScopeShell testId="find-your-school">
      <PageShell width="prose">
        <Heading as="h1" level="page">
          {t("title")}
        </Heading>
        <form className="mt-6 space-y-4" onSubmit={(e) => void onSubmit(e)} noValidate>
          <Field label={t("label")} hint={t("hint")} error={error}>
            <Input
              value={value}
              onChange={(e) => setValue(e.target.value)}
              autoComplete="off"
              autoCapitalize="none"
              spellCheck={false}
              data-testid="find-school-input"
            />
          </Field>
          <Button type="submit" disabled={busy} data-testid="find-school-submit">
            {t("submit")}
          </Button>
        </form>
      </PageShell>
    </TenantScopeShell>
  );
}
